import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DashboardLayout } from '../common/page_component/index.js';
import Loader from '../common/Loader';
import Alert from '../common/Alert.js';
import routeService from '../../services/RouteService.js';
import { Route as RouteIcon, Pencil, ArrowLeft } from 'lucide-react';
import Button from '../common/Button.js';
import CrossingStopsView from './CrossingStopsView.js';

const valueClass =
  'w-full min-w-0 px-3 sm:px-4 py-2.5 sm:py-2 border border-gray-200 rounded-xl text-sm bg-gray-50 text-gray-800 break-words';

const hubLine = (hub) => {
  if (!hub?.location) return '—';
  return `${hub.location}${hub.location_code ? ` (${hub.location_code})` : ''}`;
};

const ViewRoute = () => {
  const { routeId } = useParams();
  const navigate = useNavigate();
  const [route, setRoute] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRoute = async () => {
      try {
        const response = await routeService.getById(routeId);
        setRoute(response.data || response);
      } catch (err) {
        Alert.error('Failed to load route details');
        console.error(err);
        navigate('/routes');
      } finally {
        setLoading(false);
      }
    };
    fetchRoute();
  }, [routeId, navigate]);

  const pathPreview = () => {
    if (!route) return '—';
    const parts = [];
    if (route.from_location?.location) parts.push(route.from_location.location);
    (route.via_locations || []).forEach((hub) => {
      if (hub.location) parts.push(hub.location);
    });
    if (route.to_location?.location) parts.push(route.to_location.location);
    return parts.join(' → ') || '—';
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <Loader fullScreen={false} size="default" message="Loading route details..." />
        </div>
      </DashboardLayout>
    );
  }

  if (!route) {
    return (
      <DashboardLayout>
        <div className="w-full min-w-0 max-w-full mx-auto">
          <p className="text-gray-600">Route not found</p>
        </div>
      </DashboardLayout>
    );
  }

  const viaStops = route.via_locations || [];
  const crossingStops = route.crossing_stops || [];

  return (
    <DashboardLayout>
      <div className="w-full min-w-0 max-w-full mx-auto">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
          <h1 className="text-2xl sm:text-xl font-bold text-secondary flex items-center gap-2 sm:gap-3 min-w-0">
            <RouteIcon className="w-6 h-6 text-secondary shrink-0" />
            <span className="truncate">{route.name || 'Route'}</span>
          </h1>
          <div className="flex items-center gap-2 shrink-0">
            <Button
              variant="primary"
              icon={Pencil}
              onClick={() => navigate(`/routes/edit/${routeId}`)}
            >
              Edit Route
            </Button>
            <button
              onClick={() => navigate('/routes')}
              className="group flex items-center justify-center gap-2 px-3 sm:px-3 py-2.5 sm:py-2 text-xs sm:text-sm text-buttonBg bg-buttonTxt hover:text-buttonTxt hover:bg-buttonBg border border-current rounded-xl transition-all duration-300 font-medium whitespace-nowrap"
            >
              <span className="group-hover:-translate-x-1 transition-transform"><ArrowLeft className="w-4 h-4" /></span>
              Back to Routes
            </button>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-4 sm:p-8 space-y-6 sm:space-y-8">
          <div>
            <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
              Route Information
            </h3>
            <p className="text-xs sm:text-sm text-gray-600 leading-relaxed mb-4 sm:mb-6">
              Travel order for this route. ML sheets that use it load parcels farthest-first.
              Crossing hubs are the stops where goods switch. Under each hub are the locations those goods cross to.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 md:gap-6">
              <div className="sm:col-span-2">
                <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                  Route Name
                </label>
                <div className={valueClass}>{route.name || '—'}</div>
                <p className="text-[11px] sm:text-xs text-gray-500 mt-1">Route ID: {route.route_id}</p>
              </div>

              <div className="min-w-0">
                <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                  From Location
                </label>
                <div className={valueClass}>{hubLine(route.from_location)}</div>
              </div>

              <div className="min-w-0">
                <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                  To Location
                </label>
                <div className={valueClass}>{hubLine(route.to_location)}</div>
              </div>

              <div className="sm:col-span-2 min-w-0">
                <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                  Via Stops (travel order)
                </label>
                {viaStops.length === 0 ? (
                  <p className="text-[11px] sm:text-xs text-gray-500">No middle stops — direct from → to.</p>
                ) : (
                  <ul className="space-y-1.5 sm:space-y-2">
                    {viaStops.map((hub, index) => (
                      <li
                        key={hub.id || index}
                        className="px-2.5 sm:px-3 py-2 border border-gray-200 rounded-xl bg-gray-50 text-xs sm:text-sm text-gray-800"
                      >
                        {index + 1}. {hubLine(hub)}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="sm:col-span-2 space-y-2">
                <label className="block text-xs sm:text-sm font-medium text-secondary">
                  Crossing hubs
                </label>
                <CrossingStopsView stops={crossingStops} />
              </div>

              <div className="sm:col-span-2 rounded-xl bg-orange-50 border border-orange-100 px-3 sm:px-4 py-2.5 sm:py-3">
                <p className="text-[11px] sm:text-xs font-medium text-orange-800 mb-0.5 sm:mb-1">
                  Travel path preview
                </p>
                <p className="text-xs sm:text-sm text-orange-900 font-semibold break-words">
                  {pathPreview()}
                </p>
                <p className="text-[11px] sm:text-xs text-orange-700 mt-1">
                  Loading order on ML sheet PDF: reverse of this path (last stop first).
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ViewRoute;
